import { html, render, type TemplateResult } from 'lit-html';
import styles from './pk-admin-table.css?inline';
import { withCsrf } from '../../lib/csrf.js';

/**
 * Per-column descriptor for `<pk-admin-table>`. Pass an array of these as
 * the `columns` JSON attribute.
 *
 *   - `key`         — property name on the row record.
 *   - `label`       — column header text.
 *   - `sortable?`   — opt the column into client-side sort. Default false.
 *   - `filterable?` — include the column's stringified value in the active
 *                     `filter` substring search. Default true.
 *   - `actions?`    — when set, the column renders an array of
 *                     `{ label, action }` buttons per row. Clicking one fires
 *                     `pk-row-action` with `{ rowId, action }`.
 *   - `idKey?`      — only meaningful on an `actions` column: which row
 *                     property to surface as `rowId`. Defaults to `'id'`.
 */
export interface PkAdminTableColumn {
    key:         string;
    label:       string;
    sortable?:   boolean;
    filterable?: boolean;
    actions?:    Array<{ label: string; action: string; destructive?: boolean }>;
    idKey?:      string;
}

type SortDir = 'asc' | 'desc';

/**
 * `<pk-admin-table>` — admin-portal sortable + filterable + paginated table.
 *
 * Distinct from `<pk-data-table>` in three places:
 *
 *   1. Sort + filter + paginate all run client-side over the loaded rows
 *      (vs `<pk-data-table>`'s server cursor pagination).
 *   2. CSV export is built-in (Blob + download anchor over the currently-loaded
 *      rows — does not refetch all pages).
 *   3. Action columns: per-row buttons emit a single `pk-row-action` event with
 *      `{ rowId, action }`, so a write-side admin row that needs N actions
 *      doesn't need N event handlers.
 *
 * Hydration:
 *
 *   - When `src` is set, the table GETs the URL and expects
 *     `{ items, total, page, pageSize }` (or `{ items, total }`).
 *   - When `src` is absent, the host can either call `setRows(rows)` or
 *     slot a default-slot child `<script type="application/json">` whose
 *     text is `{ items: [...] }`. The slot form is the easy Razor path.
 *
 * Attributes:
 *   - `src`          — URL to GET. Optional; if absent, the table runs over
 *                       statically-provided data.
 *   - `columns`      — JSON `PkAdminTableColumn[]`. Required for non-trivial
 *                       use.
 *   - `sort`         — initial sort, formatted as `"<key>:asc"` or
 *                       `"<key>:desc"`. Optional.
 *   - `filter`       — initial filter substring. Optional.
 *   - `paginate`     — boolean; when present, render pager + slice client-side.
 *                       Defaults to `false` (show all loaded rows).
 *   - `page-size`    — integer; default 25. Only consulted when `paginate`.
 *   - `export-csv`   — boolean; render an "Export CSV" button in the toolbar.
 *
 * Events:
 *   - `pk-row-action` — `CustomEvent<{ rowId: string; action: string }>` fired
 *                       when a per-row action button is clicked.
 */
export class PkAdminTable extends HTMLElement {
    static readonly tagName = 'pk-admin-table';

    static get observedAttributes(): string[] {
        return ['src', 'columns', 'sort', 'filter', 'paginate', 'page-size', 'export-csv'];
    }

    private readonly _root: ShadowRoot;
    private _rows: Array<Record<string, unknown>> = [];
    private _columns: PkAdminTableColumn[] = [];
    private _sortKey:  string  = '';
    private _sortDir:  SortDir = 'asc';
    private _filter:   string  = '';
    private _page:     number  = 0;
    private _loadState: 'idle' | 'loading' | 'ok' | 'error' = 'idle';
    private _errorMsg: string = '';

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this._readAttributes();
        this.render();
        if (this.getAttribute('src')) {
            void this._loadData();
        } else {
            this._tryHydrateFromSlot();
        }
    }

    attributeChangedCallback(name: string): void {
        if (!this.isConnected) return;
        if (name === 'src') {
            const src = this.getAttribute('src');
            if (src) { void this._loadData(); return; }
        }
        if (name === 'columns') {
            this._readColumns();
        }
        if (name === 'sort') {
            this._readSort();
        }
        if (name === 'filter') {
            this._filter = this.getAttribute('filter') ?? '';
            this._page = 0;
        }
        if (name === 'paginate' || name === 'page-size' || name === 'export-csv') {
            // Layout-affecting; just re-render.
        }
        this.render();
    }

    /** Programmatically set the rows (skips fetch). */
    setRows(rows: Array<Record<string, unknown>>): void {
        this._rows = rows;
        this._loadState = 'ok';
        this._page = 0;
        this.render();
    }

    private _readAttributes(): void {
        this._readColumns();
        this._readSort();
        this._filter = this.getAttribute('filter') ?? '';
    }

    private _readColumns(): void {
        const raw = this.getAttribute('columns');
        if (!raw) { this._columns = []; return; }
        try {
            const parsed = JSON.parse(raw) as unknown;
            this._columns = Array.isArray(parsed) ? parsed as PkAdminTableColumn[] : [];
        } catch {
            this._columns = [];
        }
    }

    private _readSort(): void {
        const raw = this.getAttribute('sort') ?? '';
        if (!raw) { this._sortKey = ''; return; }
        const [key, dir] = raw.split(':');
        this._sortKey = key ?? '';
        this._sortDir = dir === 'desc' ? 'desc' : 'asc';
    }

    private _tryHydrateFromSlot(): void {
        // Look for a <script type="application/json"> child to seed rows. This
        // is the easy Razor-server-side path: emit the rows into the HTML
        // without any portal-kit fetch involvement.
        const script = this.querySelector('script[type="application/json"]');
        if (!script) {
            this._loadState = 'ok';
            return;
        }
        try {
            const parsed = JSON.parse(script.textContent ?? '{}') as { items?: unknown };
            const items  = Array.isArray(parsed.items) ? parsed.items as Array<Record<string, unknown>> : [];
            this._rows = items;
            this._loadState = 'ok';
        } catch (err) {
            this._loadState = 'error';
            this._errorMsg  = `Failed to parse data slot: ${err instanceof Error ? err.message : String(err)}`;
        }
        this.render();
    }

    private async _loadData(): Promise<void> {
        const src = this.getAttribute('src');
        if (!src) return;
        this._loadState = 'loading';
        this.render();
        try {
            const resp = await fetch(src, withCsrf());
            if (!resp.ok) {
                throw new Error(`HTTP ${resp.status}`);
            }
            const data = await resp.json() as { items?: unknown };
            this._rows = Array.isArray(data.items) ? data.items as Array<Record<string, unknown>> : [];
            this._loadState = 'ok';
        } catch (err) {
            this._loadState = 'error';
            this._errorMsg  = err instanceof Error ? err.message : String(err);
        }
        this.render();
    }

    // ── Derived view state ─────────────────────────────────────────────────

    private _pageSize(): number {
        const v = Number(this.getAttribute('page-size') ?? '25');
        return Number.isFinite(v) && v > 0 ? Math.floor(v) : 25;
    }

    private _stringForFilter(row: Record<string, unknown>): string {
        return this._columns
            .filter(c => c.filterable !== false && !c.actions)
            .map(c => {
                const v = row[c.key];
                return v == null ? '' : String(v);
            })
            .join(' ');
    }

    private _filteredRows(): Array<Record<string, unknown>> {
        const filter = this._filter.trim().toLowerCase();
        if (!filter) return this._rows;
        return this._rows.filter(r => this._stringForFilter(r).toLowerCase().includes(filter));
    }

    private _sortedRows(): Array<Record<string, unknown>> {
        const rows = [...this._filteredRows()];
        const key  = this._sortKey;
        if (!key) return rows;
        const dir = this._sortDir === 'desc' ? -1 : 1;
        rows.sort((a, b) => {
            const av = a[key];
            const bv = b[key];
            if (av === bv) return 0;
            if (av == null) return 1;
            if (bv == null) return -1;
            // Compare numbers numerically, everything else by locale string.
            const an = Number(av);
            const bn = Number(bv);
            if (Number.isFinite(an) && Number.isFinite(bn)) {
                return (an < bn ? -1 : 1) * dir;
            }
            return String(av).localeCompare(String(bv)) * dir;
        });
        return rows;
    }

    private _visibleRows(): { rows: Array<Record<string, unknown>>; total: number; page: number; pages: number } {
        const sorted = this._sortedRows();
        const paginate = this.hasAttribute('paginate');
        if (!paginate) {
            return { rows: sorted, total: sorted.length, page: 0, pages: 1 };
        }
        const pageSize = this._pageSize();
        const pages = Math.max(1, Math.ceil(sorted.length / pageSize));
        const page  = Math.min(this._page, pages - 1);
        const start = page * pageSize;
        return { rows: sorted.slice(start, start + pageSize), total: sorted.length, page, pages };
    }

    // ── Event handlers ─────────────────────────────────────────────────────

    private readonly _onHeaderClick = (col: PkAdminTableColumn): void => {
        if (!col.sortable) return;
        if (this._sortKey === col.key) {
            this._sortDir = this._sortDir === 'asc' ? 'desc' : 'asc';
        } else {
            this._sortKey = col.key;
            this._sortDir = 'asc';
        }
        this.render();
    };

    private readonly _onFilterInput = (e: Event): void => {
        const input = e.target as HTMLInputElement;
        this._filter = input.value;
        this._page = 0;
        this.render();
    };

    private readonly _onPagePrev = (): void => {
        if (this._page > 0) {
            this._page--;
            this.render();
        }
    };

    private readonly _onPageNext = (): void => {
        const { pages, page } = this._visibleRows();
        if (page < pages - 1) {
            this._page++;
            this.render();
        }
    };

    private readonly _onRowAction = (rowId: string, action: string): void => {
        this.dispatchEvent(new CustomEvent('pk-row-action', {
            detail:   { rowId, action },
            bubbles:  true,
            composed: true,
        }));
    };

    private readonly _onExportCsv = (): void => {
        const sorted = this._sortedRows();
        const cols   = this._columns.filter(c => !c.actions);
        const header = cols.map(c => csvCell(c.label)).join(',');
        const lines  = sorted.map(r => cols.map(c => csvCell(r[c.key])).join(','));
        const csv    = [header, ...lines].join('\n');
        const blob   = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        const url    = URL.createObjectURL(blob);
        const a      = document.createElement('a');
        a.href = url;
        a.download = (this.getAttribute('csv-filename') ?? 'export') + '.csv';
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        // Defer revoke so the click + navigation can resolve.
        setTimeout(() => URL.revokeObjectURL(url), 0);
    };

    // ── Templates ──────────────────────────────────────────────────────────

    private _cellTpl(row: Record<string, unknown>, col: PkAdminTableColumn): TemplateResult {
        if (col.actions && col.actions.length > 0) {
            const rowId = String(row[col.idKey ?? 'id'] ?? '');
            return html`
                <div class="cell-actions">
                    ${col.actions.map(a => html`
                        <button
                            type="button"
                            class="row-action ${a.destructive ? 'destructive' : ''}"
                            data-action=${a.action}
                            @click=${() => this._onRowAction(rowId, a.action)}
                        >${a.label}</button>
                    `)}
                </div>
            `;
        }
        const v = row[col.key];
        return html`${v == null ? '—' : String(v)}`;
    }

    private _headerCellTpl(col: PkAdminTableColumn): TemplateResult {
        const sortable = !!col.sortable;
        const sorted   = sortable && this._sortKey === col.key;
        const arrow    = sorted ? (this._sortDir === 'asc' ? ' ▲' : ' ▼') : '';
        return html`
            <th
                role="columnheader"
                class="${sortable ? 'sortable' : ''} ${sorted ? 'sorted' : ''}"
                aria-sort=${sorted ? (this._sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                @click=${() => this._onHeaderClick(col)}
            >${col.label}${arrow}</th>
        `;
    }

    private _toolbarTpl(): TemplateResult {
        const exportCsv = this.hasAttribute('export-csv');
        return html`
            <div class="toolbar">
                <input
                    type="search"
                    class="filter-input"
                    placeholder="Filter…"
                    .value=${this._filter}
                    @input=${this._onFilterInput}
                    aria-label="Filter rows"
                />
                ${exportCsv ? html`
                    <button type="button" class="export-btn" @click=${this._onExportCsv}>
                        Export CSV
                    </button>
                ` : ''}
            </div>
        `;
    }

    private _pagerTpl(total: number, page: number, pages: number): TemplateResult {
        const pageSize = this._pageSize();
        const start    = total === 0 ? 0 : page * pageSize + 1;
        const end      = Math.min(total, (page + 1) * pageSize);
        return html`
            <div class="pager">
                <span class="pager-info">${start}–${end} of ${total}</span>
                <button type="button" class="pager-btn"
                    ?disabled=${page === 0}
                    @click=${this._onPagePrev}
                    aria-label="Previous page">‹ Prev</button>
                <button type="button" class="pager-btn"
                    ?disabled=${page >= pages - 1}
                    @click=${this._onPageNext}
                    aria-label="Next page">Next ›</button>
            </div>
        `;
    }

    private template(): TemplateResult {
        const cols = this._columns;
        const paginate = this.hasAttribute('paginate');
        const { rows, total, page, pages } = this._visibleRows();

        return html`
            <style>${styles}</style>
            ${this._toolbarTpl()}
            <div class="table-wrap" aria-busy=${this._loadState === 'loading' ? 'true' : 'false'}>
                <table role="table">
                    <thead>
                        <tr role="row">
                            ${cols.map(c => this._headerCellTpl(c))}
                        </tr>
                    </thead>
                    <tbody>
                        ${this._loadState === 'error' ? html`
                            <tr><td class="error-cell" colspan=${cols.length || 1} role="alert">${this._errorMsg}</td></tr>
                        ` : rows.length === 0 ? html`
                            <tr><td class="empty-cell" colspan=${cols.length || 1}>No rows.</td></tr>
                        ` : rows.map(r => html`
                            <tr role="row">
                                ${cols.map(c => html`<td role="cell">${this._cellTpl(r, c)}</td>`)}
                            </tr>
                        `)}
                    </tbody>
                </table>
            </div>
            ${paginate ? this._pagerTpl(total, page, pages) : ''}
            <slot style="display: none;"></slot>
        `;
    }

    private render(): void {
        render(this.template(), this._root);
    }
}

function csvCell(v: unknown): string {
    if (v == null) return '';
    const s = String(v);
    // RFC 4180: quote when comma, newline, or quote present; escape internal quotes.
    if (/[",\n\r]/.test(s)) {
        return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
}

if (!customElements.get(PkAdminTable.tagName)) {
    customElements.define(PkAdminTable.tagName, PkAdminTable);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-admin-table': PkAdminTable;
    }
}
